import { formatQuantityUnit } from '../../utils/formatQuantityUnit';
import { parseQuantityUnitFromBillConfig } from './parse';

/** 直建默认数量单位（计价未选中或无 quantityUnit 时回退） */
export const DEFAULT_QUANTITY_UNITS = {
  cpu: 'Core',
  memory: 'Gi',
  gpu: 'Card',
  disk: 'Gi',
  bandwidth: 'Mbps',
} as const;

export type QuantityResourceKind = keyof typeof DEFAULT_QUANTITY_UNITS;

/** 规范化数量单位字符串 */
export function normalizeQuantityUnit(unit?: unknown): string | undefined {
  if (unit == null) return undefined;
  const text = String(unit).trim();
  return text || undefined;
}

/**
 * 解析展示/询价用数量单位：
 * - 优先 meta.quantityUnit（选型持久化）
 * - 其次 billConfig 内 quantityUnit
 * - 最后 defaultUnit（直建默认）
 */
export function resolveQuantityUnit(params: { quantityUnit?: unknown; billConfig?: unknown; defaultUnit?: string }): string | undefined {
  return (
    normalizeQuantityUnit(params.quantityUnit) ||
    parseQuantityUnitFromBillConfig(params.billConfig) ||
    normalizeQuantityUnit(params.defaultUnit)
  );
}

/**
 * 卡片 / 弹窗数量后缀统一决议。
 * - lockDisplayToDefault（存储类）：始终 defaultUnit / directLabel（表单数量为 Gi）
 * - 有选型 quantityUnit → 用选型
 * - 计价未选型 → defaultUnit（如 Core / Gi / Card）
 * - 直建 → directLabel（如 i18n「核」）或 defaultUnit；GPU 直建可省略单位
 */
export function resolveDisplayUnit(params: {
  quantityUnit?: unknown;
  /** 是否计价场景（有 billModelId） */
  pricing?: boolean;
  /** 资源类型默认单位 */
  defaultUnit: string;
  /** 直建展示文案（如 CPU 的 i18n「核」）；缺省用 defaultUnit */
  directLabel?: string;
  /**
   * 直建且无选型时是否省略单位（GPU 卡片「型号 × 数量」现状）。
   * 仅在 !pricing 时生效。
   */
  omitWhenDirect?: boolean;
  /**
   * 存储类内存/磁盘：展示固定用 defaultUnit（Gi），忽略选型 quantityUnit。
   * 提交时再由 convertFormGiToBillingRequirement 换算计费单位。
   */
  lockDisplayToDefault?: boolean;
}): string | undefined {
  if (params.lockDisplayToDefault) {
    return params.directLabel || params.defaultUnit;
  }
  const fromMeta = normalizeQuantityUnit(params.quantityUnit);
  if (fromMeta) return fromMeta;
  if (params.pricing) return params.defaultUnit;
  if (params.omitWhenDirect) return undefined;
  return params.directLabel || params.defaultUnit;
}

/** 展示用数量单位（含本地化） */
export function formatQuantityUnitLabel(unit?: string, lang?: string): string {
  return formatQuantityUnit(unit, lang);
}

/** 数量 + 单位拼接；无数量时返回 fallback；无单位时仅数量 */
export function formatQuantityWithUnit(quantity: unknown, unit?: string, fallback = '-'): string {
  if (quantity == null || quantity === '') return fallback;
  const unitLabel = unit ? formatQuantityUnitLabel(unit) : '';
  return unitLabel ? `${quantity} ${unitLabel}` : String(quantity);
}
